// Defaults and artboard presets (DRAW_PLAN §1). Sizes are in points.
import { newId } from './ids';
import { toPt } from './units';
import type { Appearance, Artboard, ColorRef, Doc, Layer, Mat2D, Sides, Stroke, Unit } from './types';

export const IDENTITY: Mat2D = [1, 0, 0, 1, 0, 0];
export const NO_SIDES: Sides = { top: 0, right: 0, bottom: 0, left: 0 };
export const rgb = (r: number, g: number, b: number, alpha = 1): ColorRef => ({ rgb: [r, g, b], alpha });
export const BLACK = rgb(0, 0, 0);
export const WHITE = rgb(1, 1, 1);

export function defaultStroke(color: ColorRef = BLACK, width = 1): Stroke {
  return {
    paint: { kind: 'solid', color },
    width,
    cap: 'butt',
    join: 'miter',
    miter: 10,
    dash: [],
    dashOffset: 0,
    align: 'center',
    opacity: 1,
    blend: 'normal',
  };
}
export function defaultAppearance(
  fill: ColorRef | null = WHITE,
  stroke: ColorRef | null = BLACK,
): Appearance {
  return {
    fills: fill ? [{ paint: { kind: 'solid', color: fill }, opacity: 1, blend: 'normal' }] : [],
    strokes: stroke ? [defaultStroke(stroke)] : [],
    opacity: 1,
    blend: 'normal',
    effects: [],
  };
}

export type ArtboardPreset = {
  id: string;
  name: string;
  nameEs: string;
  w: number;
  h: number;
  unit: Unit;
  dpi: number;
  kind: 'print' | 'screen';
};
/** DRAW_PLAN §1 presets. Screen sizes are pixels at 72 dpi (1 px = 1 pt); print sizes are physical. */
export const ARTBOARD_PRESETS: ArtboardPreset[] = [
  { id: 'letter', name: 'Letter', nameEs: 'Carta', w: 8.5, h: 11, unit: 'in', dpi: 300, kind: 'print' },
  { id: 'a4', name: 'A4', nameEs: 'A4', w: 210, h: 297, unit: 'mm', dpi: 300, kind: 'print' },
  { id: 'a3', name: 'A3', nameEs: 'A3', w: 297, h: 420, unit: 'mm', dpi: 300, kind: 'print' },
  { id: 'tabloid', name: 'Tabloid', nameEs: 'Tabloide', w: 11, h: 17, unit: 'in', dpi: 300, kind: 'print' },
  {
    id: 'ig-post',
    name: 'Instagram post 1080²',
    nameEs: 'Publicación de Instagram 1080²',
    w: 1080,
    h: 1080,
    unit: 'px',
    dpi: 72,
    kind: 'screen',
  },
  {
    id: 'story',
    name: 'Story 1080×1920',
    nameEs: 'Historia 1080×1920',
    w: 1080,
    h: 1920,
    unit: 'px',
    dpi: 72,
    kind: 'screen',
  },
  {
    id: 'menu-board',
    name: 'Menu board 3840×2160',
    nameEs: 'Pantalla de menú 3840×2160',
    w: 3840,
    h: 2160,
    unit: 'px',
    dpi: 72,
    kind: 'screen',
  },
  {
    id: 'delivery',
    name: 'Delivery listing 1200×800',
    nameEs: 'Ficha de delivery 1200×800',
    w: 1200,
    h: 800,
    unit: 'px',
    dpi: 72,
    kind: 'screen',
  },
];

export function makeArtboard(preset: ArtboardPreset, x = 0, y = 0, name?: string): Artboard {
  return {
    id: newId('ab'),
    name: name ?? preset.name,
    x,
    y,
    w: toPt(preset.w, preset.unit, 72),
    h: toPt(preset.h, preset.unit, 72),
    bleed: preset.kind === 'print' ? { top: 9, right: 9, bottom: 9, left: 9 } : { ...NO_SIDES },
    margins: preset.kind === 'print' ? { top: 36, right: 36, bottom: 36, left: 36 } : { ...NO_SIDES },
  };
}

export function makeLayer(name = 'Layer 1', color = '#4f8cff'): Layer {
  return { id: newId('ly'), name, color, visible: true, locked: false, children: [] };
}

export function newDoc(presetId = 'letter', name = 'Untitled'): Doc {
  const preset = ARTBOARD_PRESETS.find((p) => p.id === presetId) ?? ARTBOARD_PRESETS[0]!;
  return {
    v: 1,
    id: newId('doc'),
    name,
    colorMode: 'rgb',
    rasterDpi: preset.dpi,
    artboards: [makeArtboard(preset, 0, 0, 'Artboard 1')],
    layers: [makeLayer()],
    swatches: [
      { id: 'sw-black', name: 'Black', color: BLACK, global: false },
      { id: 'sw-white', name: 'White', color: WHITE, global: false },
    ],
    swatchGroups: [],
    charStyles: [],
    paraStyles: [],
    graphicStyles: [],
    symbols: [],
    fonts: [],
    guides: [],
    units: preset.unit,
  };
}
